/**
 * Dashboard API endpoints
 */
import apiClient from './config';
import logger from '../utils/logger';

export const dashboardApi = {
  /**
   * Get consolidated initial dashboard data (optimized endpoint)
   */
  async getInitialDashboardData() {
    try {
      const response = await apiClient.get('/dashboard/initial-load-v2');
      if (response.data?.success) {
        return response.data;
      }
      return null;
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
      return null;
    }
  },

  /**
   * Get total interactions for today
   */
  async getTotalInteractions() {
    try {
      const response = await apiClient.get('/dashboard/total-interactions');
      return response.data;
    } catch (error) {
      logger.error('Error fetching total interactions:', error);
      throw error;
    }
  },

  /**
   * Get yesterday's total interactions
   */
  async getYesterdayTotalInteractions() {
    try {
      const response = await apiClient.get('/dashboard/yesterday-total-interactions');
      return response.data;
    } catch (error) {
      logger.error('Error fetching yesterday total interactions:', error);
      throw error;
    }
  },

  /**
   * Get last week's total interactions
   */
  async getLastWeekTotalInteractions() {
    try {
      const response = await apiClient.get('/dashboard/last-week-total-interactions');
      return response.data;
    } catch (error) {
      logger.error('Error fetching last week total interactions:', error);
      throw error;
    }
  },

  /**
   * Get hourly mixed comparison data
   */
  async getHourlyComparisonMixed() {
    try {
      const response = await apiClient.get('/dashboard/hourly-comparison-mixed');
      return response.data;
    } catch (error) {
      logger.error('Error fetching hourly mixed comparison:', error);
      throw error;
    }
  },

  /**
   * Get hourly comparison for specific date
   */
  async getHourlyComparisonForDate(date: string) {
    try {
      const response = await apiClient.get('/dashboard/hourly-comparison-date', {
        params: { date },
      });
      return response.data;
    } catch (error) {
      logger.error('Error fetching hourly comparison for date:', error);
      return [];
    }
  },

  /**
   * Get weekly daily data
   */
  async getWeeklyDailyData() {
    try {
      const response = await apiClient.get('/dashboard/weekly-daily-data');
      return response.data;
    } catch (error) {
      logger.error('Error fetching weekly daily data:', error);
      throw error;
    }
  },

  /**
   * Get monthly daily data
   */
  async getMonthlyDailyData() {
    try {
      const response = await apiClient.get('/dashboard/monthly-daily-data');
      return response.data;
    } catch (error) {
      logger.error('Error fetching monthly daily data:', error);
      throw error;
    }
  },

  /**
   * Get top bots for today
   */
  async getTopBots() {
    try {
      const response = await apiClient.get('/dashboard/top-bot-clients');
      return response.data;
    } catch (error) {
      logger.error('Error fetching top bots:', error);
      throw error;
    }
  },

  /**
   * Get top bots for the week
   */
  async getTopBotsWeekly() {
    try {
      const response = await apiClient.get('/dashboard/top-bots-weekly');
      return response.data;
    } catch (error) {
      logger.error('Error fetching top weekly bots:', error);
      throw error;
    }
  },

  /**
   * Get top bots for the month
   */
  async getTopBotsMonthly() {
    try {
      const response = await apiClient.get('/dashboard/top-bots-monthly');
      return response.data;
    } catch (error) {
      logger.error('Error fetching top monthly bots:', error);
      throw error;
    }
  },

  /**
   * Get current month total interactions
   */
  async getCurrentMonthTotalInteractions() {
    try {
      const response = await apiClient.get('/dashboard/current-month-total-interactions');
      return response.data;
    } catch (error) {
      logger.error('Error fetching current month total interactions:', error);
      throw error;
    }
  },

  /**
   * Get weekly total interactions
   */
  async getWeeklyTotalInteractions() {
    try {
      const response = await apiClient.get('/dashboard/weekly-total-interactions');
      return response.data;
    } catch (error) {
      logger.error('Error fetching weekly total interactions:', error);
      throw error;
    }
  },

  /**
   * Get server time
   */
  async getServerTime() {
    try {
      const response = await apiClient.get('/dashboard/server-time');
      return response.data;
    } catch (error) {
      logger.error('Error fetching server time:', error);
      throw error;
    }
  },

  /**
   * Get inactive bots
   */
  async getInactiveBots() {
    try {
      const response = await apiClient.get('/dashboard/inactive-bots');
      return response.data;
    } catch (error) {
      logger.error('Error fetching inactive bots:', error);
      throw error;
    }
  },

  /**
   * Get FD Treemap data with hourly comparison
   * Returns all bots with hourly data for today, yesterday, and last week
   */
  async getFDTreemapHourlyData() {
    try {
      const response = await apiClient.get('/dashboard/fd-treemap-hourly');
      return response.data;
    } catch (error) {
      logger.error('Error fetching FD treemap hourly data:', error);
      throw error;
    }
  },
};

export default dashboardApi;
